import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Activity, Flame, Clock, Users, Megaphone, Pencil, X } from 'lucide-react';

function getSaludo(nombre) {
  const h = new Date().getHours();
  const firstName = (nombre || 'lobo').split(' ')[0];
  if (h >= 5 && h < 12) {
    return { texto: `Buenos días, ${firstName}`, emoji: '🌅', sub: 'Hoy es un buen día para sumar' };
  }
  if (h >= 12 && h < 17) {
    return { texto: `Buenas tardes, ${firstName}`, emoji: '☀️', sub: 'A mitad del día y el Klan no para' };
  }
  if (h >= 17 && h < 22) {
    return { texto: `¡A entrenar, ${firstName}!`, emoji: '🔥', sub: 'La hora de oro del lobo' };
  }
  return { texto: `Buenas noches, ${firstName}`, emoji: '🌙', sub: 'Descansa, mañana toca seguir' };
}

export default function KlanPulse() {
  const { user } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [roster, setRoster] = useState(null);
  const [anuncio, setAnuncio] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftTexto, setDraftTexto] = useState('');
  const [draftCaduca, setDraftCaduca] = useState('');
  const [saving, setSaving] = useState(false);
  const saludo = getSaludo(user?.nombre);
  const isEntrenador = user?.role === 'entrenador';

  const load = useCallback(async () => {
    try {
      const [p, r, a] = await Promise.all([
        axios.get(`${API}/community/pulse`),
        axios.get(`${API}/community/today-roster`),
        axios.get(`${API}/community/anuncio`),
      ]);
      setPulse(p.data);
      setRoster(r.data);
      setAnuncio(a.data && a.data.texto ? a.data : null);
    } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => { if (!cancelled) await load(); };
    run();
    const interval = setInterval(run, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [load]);

  const openEditor = () => {
    setDraftTexto(anuncio?.texto || '');
    setDraftCaduca(anuncio?.caduca_en ? anuncio.caduca_en.slice(0, 16) : '');
    setEditing(true);
  };

  const saveAnuncio = async () => {
    if (!draftTexto.trim()) {
      alert('Escribe un mensaje para el Klan');
      return;
    }
    setSaving(true);
    try {
      const caduca_en = draftCaduca ? new Date(draftCaduca).toISOString() : null;
      await axios.put(`${API}/community/anuncio`, {
        texto: draftTexto.trim(),
        caduca_en,
        activo: true,
      });
      setEditing(false);
      await load();
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const removeAnuncio = async () => {
    if (!window.confirm('¿Quitar el mensaje del Klan?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/community/anuncio`);
      setEditing(false);
      await load();
    } catch (e) {
      alert('Error');
    } finally { setSaving(false); }
  };

  const nextTxt = (() => {
    if (!pulse?.next_training) return null;
    const m = pulse.next_training.en_minutos;
    if (m < 0) return null;
    if (m < 60) return `Próximo entreno en ${m} min`;
    const h = Math.round(m / 60);
    return `Próximo entreno en ${h}h`;
  })();

  return (
    <div className="mb-4 space-y-3" data-testid="klan-pulse">
      {/* Mensaje destacado del entrenador (si existe) - reemplaza al saludo */}
      {anuncio && !editing ? (
        <div
          className="relative rounded-xl border-2 border-gold/60 bg-gradient-to-br from-gold/20 via-amber-900/15 to-transparent p-4 sm:p-5"
          data-testid="anuncio-klan"
        >
          <div className="flex items-start gap-3">
            <div className="bg-gold/20 rounded-full p-2 flex-shrink-0 mt-0.5">
              <Megaphone className="h-5 w-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-gold/80 font-bold mb-0.5">
                Mensaje del Klan
              </div>
              <p className="text-white text-base sm:text-lg leading-snug whitespace-pre-line">
                {anuncio.texto}
              </p>
              {anuncio.creado_por && (
                <p className="text-[11px] text-gold/70 mt-1">— {anuncio.creado_por}</p>
              )}
            </div>
            {isEntrenador && (
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  onClick={openEditor}
                  className="p-1.5 rounded hover:bg-gold/20 text-gold/80 hover:text-gold transition-colors"
                  title="Editar mensaje"
                  data-testid="btn-editar-anuncio"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={removeAnuncio}
                  disabled={saving}
                  className="p-1.5 rounded hover:bg-red-900/30 text-red-400/70 hover:text-red-400 transition-colors"
                  title="Quitar mensaje"
                  data-testid="btn-quitar-anuncio"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Saludo personalizado (cuando no hay anuncio o se está editando con saludo de fondo) */
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-2xl">{saludo.emoji}</span>
          <h2 className="text-xl sm:text-2xl font-bold text-white">{saludo.texto}</h2>
          <span className="text-gray-500 text-sm italic">— {saludo.sub}</span>
          {isEntrenador && !editing && (
            <>
              <button
                onClick={openEditor}
                className="ml-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gold/15 hover:bg-gold/25 text-gold transition-colors"
                data-testid="btn-add-anuncio"
              >
                <Megaphone className="h-3 w-3" /> Añadir mensaje
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('¿Generar el resumen "Tu semana en MK" con los datos de ESTA semana?')) return;
                  try {
                    await axios.post(`${API}/admin/community/regenerar-resumen?semana_actual=true`);
                    // Borrar dismissed previo y recargar
                    try { localStorage.removeItem('mk_resumen_dismissed'); } catch (e) { /* ignore */ }
                    alert('✅ Resumen generado. Recarga la app para verlo.');
                    window.location.reload();
                  } catch (e) {
                    alert('Error: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                className="ml-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
                title="Generar resumen semanal manualmente"
                data-testid="btn-trigger-resumen"
              >
                📊 Generar resumen
              </button>
            </>
          )}
        </div>
      )}

      {/* Editor del anuncio */}
      {isEntrenador && editing && (
        <div className="bg-gray-900/80 border border-gold/30 rounded-xl p-4 space-y-3" data-testid="anuncio-editor">
          <div className="flex items-center justify-between">
            <h3 className="text-gold font-bold text-sm flex items-center gap-2">
              <Megaphone className="h-4 w-4" /> Mensaje del Klan
            </h3>
            <button
              onClick={() => setEditing(false)}
              className="text-gray-400 hover:text-white p-1"
              data-testid="btn-close-anuncio-editor"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <textarea
            value={draftTexto}
            onChange={(e) => setDraftTexto(e.target.value)}
            placeholder={"Ej: Sábado entreno especial en la playa de Can Picafort a las 8h. ¡Os espero!"}
            rows={3}
            className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-gold/50 focus:outline-none resize-none"
            data-testid="input-anuncio-texto"
          />
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <label className="flex items-center gap-2">
              Caduca el:
              <input
                type="datetime-local"
                value={draftCaduca}
                onChange={(e) => setDraftCaduca(e.target.value)}
                className="bg-gray-950 border border-gray-700 rounded px-2 py-1 text-white"
                data-testid="input-anuncio-caduca"
              />
            </label>
            <span className="text-gray-600">(opcional)</span>
          </div>
          <div className="flex justify-between items-center gap-2">
            <div>
              {anuncio && (
                <button
                  onClick={removeAnuncio}
                  disabled={saving}
                  className="text-[11px] text-red-400 hover:text-red-300 px-2 py-1"
                  data-testid="btn-eliminar-anuncio"
                >
                  Eliminar
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(false)}
                disabled={saving}
                className="px-3 py-1.5 rounded text-sm text-gray-300 hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={saveAnuncio}
                disabled={saving || !draftTexto.trim()}
                className="px-4 py-1.5 rounded bg-gold text-black text-sm font-bold disabled:opacity-50"
                data-testid="btn-guardar-anuncio"
              >
                {saving ? 'Guardando...' : 'Publicar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pulso del Klan (barra) */}
      {pulse && (pulse.active_today > 0 || pulse.entrenos_hoy_total > 0) && (
        <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm bg-gradient-to-r from-gray-900/80 to-gray-900/40 border border-gold/20 rounded-full px-4 py-2">
          <span className="flex items-center gap-1.5 text-emerald-400">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <b>{pulse.active_today}</b> <span className="text-gray-300">activos hoy</span>
          </span>
          {pulse.entrenos_en_marcha > 0 && (
            <span className="flex items-center gap-1 text-orange-400">
              <Flame className="h-3 w-3" /> <b>{pulse.entrenos_en_marcha}</b> en marcha
            </span>
          )}
          {pulse.entrenos_hoy_total > 0 && (
            <span className="flex items-center gap-1 text-gray-300">
              <Activity className="h-3 w-3 text-gold" /> <b>{pulse.entrenos_hoy_total}</b> entrenos hoy
            </span>
          )}
          {nextTxt && (
            <span className="flex items-center gap-1 text-gold">
              <Clock className="h-3 w-3" /> {nextTxt}
            </span>
          )}
        </div>
      )}

      {/* Roster de hoy */}
      {roster && roster.total > 0 && (
        <div className="flex items-center gap-2" data-testid="today-roster">
          <Users className="h-4 w-4 text-gold flex-shrink-0" />
          <span className="text-xs text-gray-400 flex-shrink-0">Hoy entrenan:</span>
          <div className="flex items-center -space-x-2 flex-1 overflow-hidden">
            {roster.users.slice(0, 8).map((u, i) => (
              <div
                key={u.id}
                className="w-7 h-7 rounded-full border-2 border-gray-900 overflow-hidden bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center text-[10px] text-gray-300 font-bold flex-shrink-0"
                title={u.nombre}
                style={{ zIndex: 10 - i }}
              >
                {u.foto_perfil ? (
                  <img
                    src={u.foto_perfil.startsWith('http') ? u.foto_perfil : `${BACKEND_URL}${u.foto_perfil}`}
                    alt={u.nombre}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  (u.nombre || 'U')[0].toUpperCase()
                )}
              </div>
            ))}
            {roster.total > 8 && (
              <div className="w-7 h-7 rounded-full border-2 border-gray-900 bg-gold text-gray-900 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                +{roster.total - 8}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
